/**
 * Slug utilities – UUID ↔ short base-62 encoding
 *
 * A full UUIDv4 (128 bits) encodes to exactly 22 base-62 characters.
 * The encoding is fully reversible:  shortToUuid(uuidToShort(uuid)) === uuid
 *
 * Slug format:  `{kebab-name}-{22-char-base62}`
 * Example:      `cool-venue-aB3Xy9mNpQrTuVwZ12345a`
 */

const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE = BigInt(62);
const ZERO = BigInt(0);
const SHORT_LEN = 22; // chars needed to represent 128 bits in base-62
const MAX_UUID_VALUE = BigInt("0xffffffffffffffffffffffffffffffff");
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_HEX_PATTERN = /^[0-9a-f]{32}$/i;
const SLUG_PATTERN = /^([a-z0-9]+(?:-[a-z0-9]+)*)-([0-9A-Za-z]{22})$/;

// ─── codec ────────────────────────────────────────────────────────────────────

/**
 * Encode a UUIDv4 string (with or without dashes) to a 22-character base-62 string.
 */
export function uuidToShort(uuid: string): string {
  const hex = uuid.replace(/-/g, "");
  if (!UUID_HEX_PATTERN.test(hex)) throw new Error(`Invalid UUID: ${uuid}`);

  let n = BigInt("0x" + hex);
  const chars: string[] = [];
  while (n > ZERO) {
    chars.unshift(BASE62[Number(n % BASE)] as string);
    n /= BASE;
  }
  // Left-pad with '0' to always produce exactly SHORT_LEN characters
  return chars.join("").padStart(SHORT_LEN, "0");
}

/**
 * Decode a 22-character base-62 string back to a canonical UUIDv4 string.
 */
export function shortToUuid(short: string): string {
  if (short.length !== SHORT_LEN)
    throw new Error(`Invalid short UUID: ${short}`);

  let n = ZERO;
  for (const ch of short) {
    const idx = BASE62.indexOf(ch);
    if (idx === -1) throw new Error(`Invalid char in short UUID: ${ch}`);
    n = n * BASE + BigInt(idx);
  }
  if (n > MAX_UUID_VALUE)
    throw new Error(`Short UUID is out of range: ${short}`);

  // Convert BigInt to 32-character zero-padded hex
  const hex = n.toString(16).padStart(32, "0");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

// ─── slug helpers ─────────────────────────────────────────────────────────────

/**
 * Normalise a free-form name to a URL-safe kebab string.
 * e.g. "Über Café & Bar!" → "uber-cafe-bar"
 */
export function nameToKebab(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, "-") // non-alnum → dash
    .replace(/^-+|-+$/g, "") // trim leading/trailing dashes
    .slice(0, 60) // cap length
    .replace(/-+$/g, ""); // slicing must not leave a trailing dash
}

/**
 * Build a human-readable slug from a display name and a UUIDv4.
 * e.g. makeSlug("Cool Venue", "550e8400-e29b-41d4-a716-446655440000")
 *      → "cool-venue-0VgOAAuSmU15a9DCsUAAAA"
 */
export function makeSlug(name: string, uuid: string): string {
  const prefix = nameToKebab(name) || "item";
  return `${prefix}-${uuidToShort(uuid)}`;
}

/**
 * Extract and decode the UUIDv4 embedded at the end of a slug produced by makeSlug().
 * Returns null if the slug doesn't contain a valid short UUID suffix.
 *
 * UUID-only legacy identifiers remain valid aliases. All slug-shaped identifiers
 * must otherwise match the exact format emitted by makeSlug().
 */
export function extractUuidFromSlug(slug: string): string | null {
  if (!slug) return null;

  // Case 1 – already a plain UUID (legacy URLs)
  if (UUID_PATTERN.test(slug)) return slug.toLowerCase();

  // Case 2 – canonical shape or a stale name prefix with a valid short UUID.
  const match = SLUG_PATTERN.exec(slug);
  const prefix = match?.[1];
  const short = match?.[2];
  if (!prefix || !short || prefix.length > 60) return null;

  try {
    return shortToUuid(short);
  } catch {
    return null;
  }
}

export function isCanonicalSlug(
  slug: string,
  name: string,
  uuid: string,
): boolean {
  try {
    return slug === makeSlug(name, uuid);
  } catch {
    return false;
  }
}
